import React, { createContext, useState, useCallback, useEffect } from 'react';
import { ToastContainer } from '../components/Toast/Toast';
import toastService from '../services/toast';

type ToastHandler = (options: { type?: string; message: string; duration?: number }) => string;

type ToastContextValue = {
  toasts: any[];
  addToast: ToastHandler;
  removeToast: (id: string) => void;
  error: (message: string) => string;
  success: (message: string) => string;
  info: (message: string) => string;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 6000;

export function ToastProvider({ children }: any) {
  const [toasts, setToasts] = useState<any[]>([]);

  const addToast = useCallback(({ type = 'info', message, duration = DEFAULT_DURATION }) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, type, message, duration }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    toastService.setHandler(addToast);
    return () => toastService.setHandler(null);
  }, [addToast]);

  const value = {
    toasts,
    addToast,
    removeToast,
    error: (message) => addToast({ type: 'error', message }),
    success: (message) => addToast({ type: 'success', message }),
    info: (message) => addToast({ type: 'info', message }),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}
